import { useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import tips from "../../../../assets/copy/tips";
import AutoSuggestion from "../../../../components/AutoSuggestion";
import ErrorToast from "../../../../components/ErrorToast";
import EditorListItems, {
	type EditorListItem,
} from "../../../../components/ResumeBuilder/EditorListItems/EditorListItems";
import SkipSectionDialog from "../../../../components/ResumeBuilder/SkipSectionDialog";
import Spinner from "../../../../components/Spinner";
import TextEditor from "../../../../components/TextEditor";
import Tips from "../../../../components/Tips";
import InputField from "../../../../components/UIComponents/InputField";
import SelectField from "../../../../components/UIComponents/SelectField";
import { Button } from "../../../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../../components/ui/dialog";
import ResumeContext from "../../../../context/ResumeContext";
import { NavigationHandler } from "../../../../handlers/navigationHandler";
import { resumeObjectToContextFormat } from "../../../../handlers/valueTransformation";
import { Education } from "../../../../models/resume/education";
import { educationSchema } from "../../../../schemas/resume";
import { StaticValue } from "../../../../seed/constants";
import options from "../../../../seed/dropDownOptions";
import mapping from "../../../../seed/metadata/filemapping";
import { usePatchResumeSection } from "../../../../services/create-resume/queries";
import { RESUME_SECTIONS } from "../../../../services/create-resume/resumeSections";
import type { ResumeContextValue } from "../../../../types/resume";

interface EducationFormState {
	schoolName: string;
	educationLevel: string;
	fieldOfStudyCategory: string;
	fieldOfStudy: string;
	gMonth: string | number;
	gYear: string | number;
	referenceId?: string;
}

const ResumeEducationInformation = () => {
	const navigate = useNavigate();
	const eduInfo = useSelector(
		(state: { EducationInfoReducer: { eduInfoList: EditorListItem[]; description: string } }) =>
			state.EducationInfoReducer,
	);
	const eduInfoList = eduInfo.eduInfoList;
	const [isInputCompleted, setIsInputCompleted] = useState(false);
	const navigationHandler = new NavigationHandler();
	const link = useLocation().pathname.split("/")[2];
	const patchResumeSection = usePatchResumeSection();
	const staticValue = new StaticValue();
	const staticValues = staticValue.getDefaultValues();
	const [cookies] = useCookies(["resume"]);
	const [country, setCountry] = useState("Tanzania");

	const locations = mapping["districtsRegions"][0].districtRegions;

	const { resumeData, setResumeData } = useContext(ResumeContext) as ResumeContextValue;
	let { journal } = resumeData;

	let resumeCurrentStatus = journal ? journal.currentStatus : false;

	let isComplete = resumeCurrentStatus == "COMPLETED";

	const [showSkipDialog, setShowSkipDialog] = useState(false);

	//Declaring the form states
	const [educationInfo, setEducationInfo] = useState<EducationFormState>({
		schoolName: "",
		educationLevel: "",
		fieldOfStudyCategory: "",
		fieldOfStudy: "",
		gMonth: "",
		gYear: "",
	});
	const [schoolLocation, setSchoolLocation] = useState("");

	const [editorListItems, setEditorListItems] = useState(eduInfoList);

	const [description, setDescription] = useState(eduInfo.description);
	const navigationState = useLocation().state;

	useEffect(() => {
		if (navigationState) {
			const currentEducation = resumeData.education.filter(
				(item) => item.referenceId === navigationState.referenceId,
			)[0];
			if (navigationState) {
				if (resumeData.education.length > 0) {
					setEducationInfo({
						schoolName: currentEducation.schoolName,
						educationLevel: currentEducation.educationLevel,
						fieldOfStudy: currentEducation.fieldOfStudy,
						gMonth: currentEducation.gMonth,
						gYear: currentEducation.gYear,
						referenceId: currentEducation.referenceId,
						fieldOfStudyCategory: currentEducation.fieldOfStudyCategory,
					});
					setDescription(currentEducation.description);
					const address = currentEducation.schoolCity.split(",");
					setSchoolLocation(`${address?.[0] || ""}, ${address?.[1] || ""}`);
					setCountry(address?.[2] || "");
				}
			}
		}
	}, [resumeData]);

	//The form event handler
	const onChangeInput = (e) => {
		if (e.target.name === "schoolLocation") {
			setSchoolLocation(e.target.value);
		} else if (e.target.name === "country") {
			setCountry(e.target.value);
		} else {
			setEducationInfo((prevState) => {
				return {
					...prevState,
					[e.target.name]: e.target.value,
				};
			});
		}
	};

	let formInputs = {
		...educationInfo,
		description,
		schoolLocation,
	};

	// Derive completeness from the stable state values, not the `formInputs`
	// object literal (which is recreated every render and would make this effect
	// run on every render).
	useEffect(() => {
		const { schoolName, educationLevel, fieldOfStudy, gMonth, gYear, fieldOfStudyCategory } = educationInfo;

		setIsInputCompleted(
			educationSchema.safeParse({
				schoolName,
				educationLevel,
				fieldOfStudy,
				gMonth,
				gYear,
				fieldOfStudyCategory,
				schoolLocation: `${schoolLocation}, ${country}`,
			}).success,
		);
	}, [educationInfo, schoolLocation, country]);

	const skipSection = () => {
		navigationHandler.getNext(navigationHandler.template.navigationFlow, link, isComplete);
		navigate("/resume/education-summary");
	};

	const [serverErrors, setServerErrors] = useState([]);

	function patchSection() {
		const {
			schoolName,
			schoolLocation,
			educationLevel,
			fieldOfStudy,
			fieldOfStudyCategory,
			gMonth,
			gYear,
			description,
		} = formInputs;

		const address = schoolLocation.split(",");

		const payload = {
			city: address[0] || "",
			region: address[1] || "",
			country: country,
			schoolName,
			schoolLocation,
			educationLevel,
			fieldOfStudy,
			fieldOfStudyCategory,
			gMonth,
			gYear,
			description,
		};

		const validation = educationSchema.safeParse({ ...formInputs, schoolLocation: `${schoolLocation}, ${country}` });
		if (validation.success) {
			const _section = "EDUCATION";
			const eduPayload = new Education(payload);
			const eduReq = eduPayload.getEducationRequest();

			if (educationInfo.referenceId !== "") {
				eduReq.referenceId = educationInfo.referenceId;
			}

			const resumeId = cookies[staticValues.localStorageResumeId];

			patchResumeSection.mutate({ resumeId, section: RESUME_SECTIONS.EDUCATION, data: eduReq } as never, {
				onSuccess: successPatchEducation,
				onError: errorPatchEducation,
			});
		} else {
			setServerErrors(validation.error.issues.map((issue) => issue.message));
		}
	}

	const [showDetailsModal, setShowDetailsModal] = useState(false);

	function nextButton(e) {
		e.preventDefault();
		setServerErrors([]);
		//TODO set navigation state complete
		formInputs = {
			...formInputs,
			schoolLocation: `${formInputs.schoolLocation}, ${country}`,
		};

		const validation = educationSchema.safeParse(formInputs);
		if (validation.success) {
			return setShowDetailsModal(true);
		}
		return setServerErrors(validation.error.issues.map((issue) => issue.message));
	}

	function successPatchEducation(ledger) {
		resumeObjectToContextFormat(ledger, setResumeData);

		navigate("/resume/education-summary");
	}

	function errorPatchEducation(data) {
		const serverResponse = data.response.data;
		Array.isArray(serverResponse.message)
			? setServerErrors((prev) => {
					return [...prev, ...serverResponse.message];
				})
			: setServerErrors((prev) => {
					return [...prev, serverResponse.message];
				});
	}

	useEffect(() => {
		if (educationInfo.educationLevel === "O-Level" || educationInfo.educationLevel === "A-Level") {
			setEducationInfo((prev) => {
				return {
					...prev,
					fieldOfStudyCategory: "Secondary Education",
				};
			});
		}
	}, [educationInfo.educationLevel]);

	return (
		<>
			<SkipSectionDialog open={showSkipDialog} onOpenChange={setShowSkipDialog} onSkip={skipSection} />

			<div className="min-h-screen">
				{serverErrors.map((item, index) => {
					return <ErrorToast error={item} key={index} />;
				})}
				<div className="mb-3">
					<p>List your academic credentials, starting with the most recent degree.</p>
				</div>
				<div className="md:w-2/3">
					<div className="mb-2 flex justify-end">
						<Tips content={tips.educationHistory} />
					</div>
					<form action="">
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
							<div className="md:col-span-2">
								<SelectField
									name="educationLevel"
									value={String(educationInfo.educationLevel)}
									onChange={onChangeInput}
									options={options.educationLevels}
									label="Education Levels"
								/>
							</div>

							<div className="md:col-span-2">
								<SelectField
									name="fieldOfStudyCategory"
									value={educationInfo.fieldOfStudyCategory}
									onChange={onChangeInput}
									options={options.fieldsOfStudy}
									label="Field of Study"
								/>
							</div>

							<div className="md:col-span-2">
								<InputField
									type="text"
									label="Degree/Certificate Major/Combination"
									name="fieldOfStudy"
									value={educationInfo.fieldOfStudy}
									onChange={onChangeInput}
									placeholder="Ex: BSc, Computer Science"
								/>
							</div>

							<div className="md:col-span-2">
								<InputField
									type="text"
									label="School Name"
									name="schoolName"
									value={educationInfo.schoolName}
									onChange={onChangeInput}
									placeholder="Enter School Name"
								/>
							</div>

							<div>
								<SelectField
									name="country"
									value={country}
									onChange={onChangeInput}
									options={options.phoneCountryCodeOptions}
									label="Country"
								/>
							</div>

							<div className="relative">
								{!(country === "Tanzania") && (
									<InputField
										type="text"
										label="Ex: City, Region"
										name="schoolLocation"
										value={schoolLocation}
										onChange={onChangeInput}
										placeholder="City, Region"
									/>
								)}
								{country === "Tanzania" && (
									<AutoSuggestion
										suggestions={locations}
										label="Eg: District, Region"
										type="text"
										name="schoolLocation"
										userInput={schoolLocation}
										setUserInput={setSchoolLocation}
										isForm={true}
									/>
								)}
							</div>

							<div>
								<SelectField
									name="gMonth"
									value={String(educationInfo.gMonth)}
									onChange={onChangeInput}
									options={options.months}
									label="Graduation Month"
								/>
							</div>

							<div>
								<SelectField
									name="gYear"
									value={String(educationInfo.gYear)}
									onChange={onChangeInput}
									options={options.years(40)}
									label="Graduation Year"
								/>
							</div>
							<div className="col-span-2 flex items-center justify-end">
								<Button type="button" variant="ghost" onClick={() => navigate("/resume/skills")}>
									Skip for now
								</Button>
								<Button
									type="button"
									onClick={(e) => {
										if (!isInputCompleted) {
											setShowSkipDialog(true);
										}
										nextButton(e);
									}}
								>
									{patchResumeSection.isPending ? (
										<>
											Loading <Spinner />
										</>
									) : (
										"Continue"
									)}
								</Button>
							</div>
						</div>
					</form>
				</div>

				<Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
					<DialogContent className="sm:max-w-[80vw]">
						<DialogHeader>
							<DialogTitle className="font-bold text-base">Activities or Relevant Course Work</DialogTitle>
						</DialogHeader>
						{serverErrors.map((item, index) => {
							return <ErrorToast error={item} key={index} />;
						})}
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
							<div>
								<div>
									<p className="mb-0">
										<span>{formInputs.educationLevel}</span>, <span>{formInputs.fieldOfStudy}</span>
									</p>
									<p className="mb-3 text-gray-500">
										{formInputs.schoolName} &bull; {formInputs.gYear}
									</p>
								</div>
								<div>
									<EditorListItems
										editorListItems={editorListItems}
										editorContent={description}
										setEditorContent={setDescription}
										setEditorListItems={setEditorListItems}
									/>
								</div>
							</div>
							<div className="mt-4 flex flex-col justify-end">
								<div className="w-full">
									<TextEditor value={description} setValue={setDescription} placeholder="Add a description." />
								</div>
							</div>
							<div className="my-4 flex items-center justify-end md:col-span-2">
								<button
									type="button"
									className="mr-3 cursor-pointer font-semibold text-primary-700 text-sm"
									onClick={() => patchSection()}
								>
									Skip for now
								</button>
								<Button
									type="button"
									size="lg"
									className="rounded-full"
									onClick={() => {
										if (!isInputCompleted) {
											setShowSkipDialog(true);
										}
										const { description } = formInputs;
										const formErrors = [];

										if (description.includes("{") || description.includes("}")) {
											formErrors.push("Please edit the suggested entries on the description");
										}

										if (formErrors.length === 0) {
											return patchSection();
										}
										return setServerErrors(formErrors);
									}}
								>
									{patchResumeSection.isPending ? (
										<>
											Loading <Spinner />
										</>
									) : (
										"Continue"
									)}
								</Button>
							</div>
						</div>
					</DialogContent>
				</Dialog>
			</div>
		</>
	);
};

export default ResumeEducationInformation;
